/**
 * Turning a Salesforce project into the string the DANI planner expects.
 *
 * There is no project-number column anywhere. `survey.project_name` carries the
 * number inside the name, and `salesforce_project_id` is a different id
 * entirely — `salesforce_project_id = 29295` matches zero surveys, and roughly a
 * third of all survey rows have it set to `0`. So the reference the planner
 * receives can only ever be derived from the name.
 *
 * Confirmed with Aaron 2026-08-25: the expected shape is `NNNNN-Name`, e.g.
 * `29295-DixiePriceOptimization`. Stored project names commonly carry a
 * `YYYY-MM-` date prefix in front of that (`2024-04-29295-DixiePriceOptimization`),
 * which is what this strips.
 *
 * What it deliberately does NOT strip is anything *after* the name. Survey-level
 * names append qualifiers (" CLIENT TEST Q3", " - SCREENER", " - IN STORE") that
 * distinguish surveys within one project, and there is no reliable rule for
 * telling those from a legitimate part of a project's name. Project-level names
 * from `salesforceProjects` should not have them; if one slips through, the
 * builder shows the derived value in an editable field so the user can see it and
 * fix it before generating — which is also why this never throws.
 */

/** Leading `YYYY-MM-` on a stored project name. */
const DATE_PREFIX = /^\s*\d{4}-\d{2}-(?=\d)/;

/**
 * Derives the planner's project reference from a stored project name.
 * Returns a trimmed string; input that matches no known shape is passed through
 * unchanged rather than mangled.
 */
export function toProjectRef(projectName: string): string {
    return projectName.replace(DATE_PREFIX, '').trim();
}

/**
 * True when a reference looks like the `NNNNN-Name` shape Aaron confirmed.
 * Used only to decide whether to warn in dev — never to block a value, since a
 * project could legitimately be named something this does not anticipate.
 *
 * The date-prefix rejection is the point of this function, not an afterthought:
 * a leading `2024-` is itself four digits followed by a dash, so a naive
 * `^\d{3,6}-` check calls `2024-04-29295-Dixie` well-formed and the one mistake
 * this is meant to catch sails through.
 */
export function looksLikeProjectRef(ref: string): boolean {
    const trimmed = ref.trim();
    if (DATE_PREFIX.test(trimmed)) return false;
    return /^\d{3,6}-\S/.test(trimmed);
}
